import { useState } from 'react';
import { useInvoice } from '@/hooks/useInvoice';
import { useSubscription } from '@/hooks/useSubscription';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Link, useSearchParams } from 'react-router-dom';
import { format, subMonths } from 'date-fns';
import { formatCurrency } from '@/lib/utils';
import {
  TrendingUp,
  DollarSign,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Building2,
  Users,
  Crown,
  ArrowRight,
  PieChart as PieChartIcon,
  BarChart3,
} from 'lucide-react';

interface MonthData {
  key: string;
  label: string;
  paid: number;
  invoiced: number;
}

// Mock data generator for dashboard preview / demo rendering
const generateDemoData = () => {
  const dummyInvoices: any[] = [];
  const dummyBusinesses = [
    { businessId: 'bus_1', name: 'Alpha Tech Consultancy', isDefault: true, createdAt: new Date() },
    { businessId: 'bus_2', name: 'Design Studio Global', isDefault: false, createdAt: new Date() }
  ];
  const dummyCustomers = [
    { customerId: 'cust_1', name: 'Acme International', companyName: 'Acme Corp', isDefault: true, createdAt: new Date() },
    { customerId: 'cust_2', name: 'Globex Corporation', companyName: 'Globex Inc', isDefault: false, createdAt: new Date() },
    { customerId: 'cust_3', name: 'Initech Software', companyName: 'Initech', isDefault: false, createdAt: new Date() },
    { customerId: 'cust_4', name: 'Umbrella Pharma', companyName: 'Umbrella Group', isDefault: false, createdAt: new Date() }
  ];

  const currencies = ['MYR', 'USD'];
  const customersList = ['Acme Corp', 'Globex Inc', 'Initech', 'Umbrella Group'];

  for (let i = 0; i < 6; i++) {
    const monthDate = subMonths(new Date(), 5 - i);
    // Create 3 invoices per month
    for (let j = 0; j < 3; j++) {
      const isPaid = j === 0 || j === 1;
      const status = isPaid ? 'paid' : (j === 2 ? 'sent' : 'draft');
      const currency = currencies[j % 2];
      const baseAmount = currency === 'MYR' 
        ? Math.round(2500 + (Math.sin(i + j) * 800) + Math.random() * 400) 
        : Math.round(800 + (Math.cos(i - j) * 200) + Math.random() * 100);
      
      dummyInvoices.push({
        invoiceId: `demo_inv_${i}_${j}`,
        businessId: j % 2 === 0 ? 'bus_1' : 'bus_2',
        invoiceNumber: `INV-2026-${i}${j}`,
        status: status,
        issueDate: monthDate,
        dueDate: new Date(monthDate.getTime() + 14 * 24 * 60 * 60 * 1000),
        currency: currency,
        billTo: {
          name: customersList[(i + j) % customersList.length],
          companyName: customersList[(i + j) % customersList.length],
          address: 'Demo Office Park, Block C',
          email: 'billing@client.com',
          phone: '+6011-2345678',
          taxNumber: 'TX-776655'
        },
        lineItems: [],
        settings: {
          headerTax: { enabled: false, taxes: [] },
          headerDiscount: { enabled: false, type: 'percentage', value: 0 },
          customTaxes: [],
          showLineTax: false,
          showLineDiscount: false,
          applyTaxOn: 'subtotal'
        },
        notes: '',
        terms: '',
        templateId: 'default',
        subtotal: baseAmount,
        totalDiscount: 0,
        totalTax: 0,
        totalAmount: baseAmount,
        createdAt: monthDate,
        updatedAt: monthDate
      });
    }
  }

  return {
    invoices: dummyInvoices,
    businesses: dummyBusinesses,
    customers: dummyCustomers,
    isPro: true,
    invoicesLoading: false
  };
};

export default function ProAnalytics() {
  const [searchParams] = useSearchParams();
  const isDemo = searchParams.get('demo') === 'true';

  let { invoices, businesses, loading: invoicesLoading } = useInvoice();
  let { isPro } = useSubscription();
  
  if (isDemo) {
    const demoData = generateDemoData();
    invoices = demoData.invoices;
    businesses = demoData.businesses as any;
    isPro = demoData.isPro;
    invoicesLoading = demoData.invoicesLoading;
  }
  
  const [selectedCurrency, setSelectedCurrency] = useState<string>(isDemo ? 'USD' : 'MYR');
  const [hoveredPointIndex, setHoveredPointIndex] = useState<number | null>(null);

  // 1. Loading State
  if (invoicesLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // 2. Determine currencies in use
  const activeCurrencies = Array.from(new Set(invoices.map((inv) => inv.currency || 'MYR')));
  const displayCurrency = activeCurrencies.includes(selectedCurrency as any)
    ? selectedCurrency
    : activeCurrencies[0] || 'MYR';

  // Helper: auto-update default currency if state not in list
  if (activeCurrencies.length > 0 && !activeCurrencies.includes(selectedCurrency as any)) {
    setSelectedCurrency(activeCurrencies[0]);
  }

  // 3. Filter invoices for calculation
  const currencyInvoices = invoices.filter((inv) => (inv.currency || 'MYR') === displayCurrency);

  // 4. Summaries
  const paidInvoices = currencyInvoices.filter((inv) => inv.status === 'paid');
  const sentInvoices = currencyInvoices.filter((inv) => inv.status === 'sent');
  const draftInvoices = currencyInvoices.filter((inv) => inv.status === 'draft');

  const totalRevenue = paidInvoices.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);
  const outstandingBalance = sentInvoices.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0) + 
                            draftInvoices.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);

  // Overdue Amount calculation
  const now = new Date();
  const overdueAmount = currencyInvoices
    .filter((inv) => inv.status !== 'paid' && inv.status !== 'cancelled' && inv.dueDate && new Date(inv.dueDate) < now)
    .reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);

  const avgInvoiceAmount = currencyInvoices.length > 0
    ? currencyInvoices.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0) / currencyInvoices.length
    : 0;

  // 5. 6-Month Billing Timeline data
  const last6Months: MonthData[] = Array.from({ length: 6 }).map((_, i) => {
    const d = subMonths(new Date(), 5 - i);
    return {
      key: format(d, 'yyyy-MM'),
      label: format(d, 'MMM yy'),
      paid: 0,
      invoiced: 0,
    };
  });

  currencyInvoices.forEach((inv) => {
    const date = inv.issueDate || inv.createdAt;
    if (!date) return;
    const key = format(date, 'yyyy-MM');
    const monthData = last6Months.find((m) => m.key === key);
    if (monthData) {
      monthData.invoiced += inv.totalAmount || 0;
      if (inv.status === 'paid') {
        monthData.paid += inv.totalAmount || 0;
      }
    }
  });

  const maxVal = Math.max(...last6Months.map((m) => Math.max(m.paid, m.invoiced)), 100);

  // Donut chart status configuration
  const statusCounts = {
    paid: currencyInvoices.filter((i) => i.status === 'paid').length,
    sent: currencyInvoices.filter((i) => i.status === 'sent').length,
    draft: currencyInvoices.filter((i) => i.status === 'draft').length,
    cancelled: currencyInvoices.filter((i) => i.status === 'cancelled').length,
  };

  const totalInvoicesCount = currencyInvoices.length;

  const donutCategories = [
    { label: 'Paid', count: statusCounts.paid, color: '#10b981', textColor: 'text-emerald-500' },
    { label: 'Sent', count: statusCounts.sent, color: '#3b82f6', textColor: 'text-blue-500' },
    { label: 'Draft', count: statusCounts.draft, color: '#f59e0b', textColor: 'text-amber-500' },
    { label: 'Cancelled', count: statusCounts.cancelled, color: '#ef4444', textColor: 'text-rose-500' },
  ].filter(cat => cat.count > 0);

  // Top Customer breakdown
  const customerRevenue: Record<string, { name: string; revenue: number; count: number }> = {};
  currencyInvoices.forEach((inv) => {
    const name = inv.billTo?.companyName || inv.billTo?.name || 'Unnamed Client';
    if (!customerRevenue[name]) {
      customerRevenue[name] = { name, revenue: 0, count: 0 };
    }
    customerRevenue[name].revenue += inv.totalAmount || 0;
    customerRevenue[name].count += 1;
  });

  const topCustomers = Object.values(customerRevenue)
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 5);

  const maxCustomerRevenue = topCustomers[0]?.revenue || 100;

  // Top Billing Business breakdown
  const businessRevenue: Record<string, { name: string; revenue: number; count: number }> = {};
  currencyInvoices.forEach((inv) => {
    const bId = inv.businessId;
    const matchedBusiness = businesses.find((b) => b.businessId === bId);
    const bName = matchedBusiness?.name || 'Primary Business';
    if (!businessRevenue[bName]) {
      businessRevenue[bName] = { name: bName, revenue: 0, count: 0 };
    }
    businessRevenue[bName].revenue += inv.totalAmount || 0;
    businessRevenue[bName].count += 1;
  });

  const topBusinesses = Object.values(businessRevenue)
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 5);

  const maxBusinessRevenue = topBusinesses[0]?.revenue || 100;

  // Paywall overlay structure for Non-Pro Users
  const renderPaywall = () => (
    <div className="relative min-h-[75vh] flex items-center justify-center p-4">
      {/* Blurred Dashboard mockup backdrop */}
      <div className="absolute inset-0 select-none pointer-events-none filter blur-[6px] opacity-40 grid grid-cols-1 lg:grid-cols-3 gap-6 p-8">
        <div className="lg:col-span-3 h-32 bg-slate-200 rounded-xl"></div>
        <div className="lg:col-span-2 h-80 bg-slate-200 rounded-xl"></div>
        <div className="h-80 bg-slate-200 rounded-xl"></div>
        <div className="h-64 bg-slate-200 rounded-xl"></div>
        <div className="h-64 bg-slate-200 rounded-xl"></div>
        <div className="h-64 bg-slate-200 rounded-xl"></div>
      </div>

      {/* Paywall Card */}
      <Card className="relative max-w-lg w-full shadow-2xl border bg-white/95 backdrop-blur-md z-10 overflow-hidden transform transition-all hover:scale-[1.01]">
        <div className="absolute top-0 inset-x-0 h-2 bg-gradient-to-r from-amber-400 via-orange-500 to-yellow-500"></div>
        <CardHeader className="text-center pt-8 pb-4">
          <div className="mx-auto w-16 h-16 bg-amber-50 border border-amber-200 rounded-2xl flex items-center justify-center mb-4 text-amber-600 animate-bounce">
            <Crown className="w-8 h-8" />
          </div>
          <CardTitle className="text-2xl font-bold tracking-tight text-slate-900">Unlock Pro Analytics</CardTitle>
          <CardDescription className="text-sm text-slate-500 px-4 mt-2">
            Get deep visual insights and tracking metrics to optimize your business operations and revenue flow.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6 px-8 pb-8">
          <div className="space-y-3.5">
            <div className="flex items-start gap-3 text-sm text-slate-700">
              <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
              <p><strong>Interactive SVG Area Charts:</strong> Track invoicing totals and collected revenues across custom time ranges.</p>
            </div>
            <div className="flex items-start gap-3 text-sm text-slate-700">
              <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
              <p><strong>Payment Status Distributions:</strong> Monitor paid, unpaid, draft, and overdue invoices with dynamic donut charts.</p>
            </div>
            <div className="flex items-start gap-3 text-sm text-slate-700">
              <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
              <p><strong>Top Clients & Businesses:</strong> Analyze your primary revenue drivers and billing profiles at a single glance.</p>
            </div>
            <div className="flex items-start gap-3 text-sm text-slate-700">
              <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
              <p><strong>Multi-Currency Filtering:</strong> Seamlessly switch statistics views if you bill in multiple currencies.</p>
            </div>
          </div>
          <div className="flex flex-col gap-3 pt-2">
            <Link to="/subscription" className="w-full">
              <Button className="w-full py-6 text-base font-semibold bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white border-0 shadow-md">
                Upgrade to Pro Plan
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/dashboard" className="w-full">
              <Button variant="ghost" className="w-full py-6 text-slate-500">
                Back to Dashboard
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  if (!isPro) {
    return renderPaywall();
  }

  // 6. Render Empty State if no invoices
  if (invoices.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-6">
          <Crown className="h-7 w-7 text-amber-500" />
          <h1 className="font-heading text-3xl font-bold">Pro Analytics</h1>
        </div>
        <Card className="border border-dashed p-12 text-center max-w-xl mx-auto mt-12 shadow-sm">
          <CardContent className="space-y-6 pt-6">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto text-muted-foreground">
              <BarChart3 className="w-8 h-8" />
            </div>
            <div className="space-y-2">
              <h3 className="text-xl font-bold">No Invoices Found</h3>
              <p className="text-muted-foreground text-sm">
                Analytics require active invoices to map revenue and calculate summaries. Create your first invoice now to populate this dashboard!
              </p>
            </div>
            <Link to="/invoices/new">
              <Button size="lg" className="mt-4">
                Create First Invoice
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Calculate coordinates for Area SVG
  const chartW = 500;
  const chartH = 150;
  const paddingX = 40;
  const paddingY = 20;

  const points = last6Months.map((m, idx) => {
    const x = paddingX + (idx / 5) * chartW;
    const yPaid = paddingY + chartH - (m.paid / maxVal) * chartH;
    const yInvoiced = paddingY + chartH - (m.invoiced / maxVal) * chartH;
    return { x, yPaid, yInvoiced, label: m.label, paid: m.paid, invoiced: m.invoiced };
  });

  // SVG Area Paths
  const invoicedLinePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.yInvoiced}`).join(' ');
  const invoicedAreaPath = `${invoicedLinePath} L ${points[points.length - 1].x} ${paddingY + chartH} L ${points[0].x} ${paddingY + chartH} Z`;

  const paidLinePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.yPaid}`).join(' ');
  const paidAreaPath = `${paidLinePath} L ${points[points.length - 1].x} ${paddingY + chartH} L ${points[0].x} ${paddingY + chartH} Z`;

  // SVG Donut calculation
  let accumulatedPercent = 0;
  const donutSlices = donutCategories.map((cat) => {
    const percent = totalInvoicesCount > 0 ? cat.count / totalInvoicesCount : 0;
    const strokeDasharray = 314.16;
    const strokeDashoffset = strokeDasharray - strokeDasharray * percent;
    const rotation = -90 + accumulatedPercent * 360;
    accumulatedPercent += percent;
    return {
      ...cat,
      percent,
      strokeDasharray,
      strokeDashoffset,
      rotation,
    };
  });

  return (
    <div className="container mx-auto px-4 py-8 space-y-8">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <h1 className="font-heading text-3xl font-bold tracking-tight">Pro Analytics</h1>
            <Badge className="bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-100 flex items-center gap-1 font-semibold text-xs">
              <Crown className="w-3.5 h-3.5" />
              PRO EXCLUSIVE
            </Badge>
          </div>
          <p className="text-muted-foreground text-sm">
            Visual metrics, billing history, and customer value trackers.
          </p>
        </div>

        {/* Currency Switcher Tabs */}
        {activeCurrencies.length > 1 && (
          <div className="flex items-center gap-2 bg-muted p-1 rounded-lg self-start md:self-center">
            {activeCurrencies.map((cur) => (
              <Button
                key={cur}
                size="sm"
                variant={displayCurrency === cur ? 'default' : 'ghost'}
                onClick={() => setSelectedCurrency(cur)}
                className="font-semibold px-4"
              >
                {cur}
              </Button>
            ))}
          </div>
        )}
      </div>

      {/* Summary Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
            <CardTitle className="text-sm font-medium">Total Paid Revenue</CardTitle>
            <div className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600">
              <CheckCircle2 className="w-4 h-4" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-600">
              {formatCurrency(totalRevenue, displayCurrency)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">From cleared invoices</p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
            <CardTitle className="text-sm font-medium">Outstanding Balance</CardTitle>
            <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-blue-600">
              <Clock className="w-4 h-4" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {formatCurrency(outstandingBalance, displayCurrency)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Draft & Sent invoices</p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
            <CardTitle className="text-sm font-medium">Overdue Invoices</CardTitle>
            <div className="w-8 h-8 rounded-full bg-rose-50 flex items-center justify-center text-rose-600">
              <AlertTriangle className="w-4 h-4" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-rose-600">
              {formatCurrency(overdueAmount, displayCurrency)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Past due and unpaid</p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
            <CardTitle className="text-sm font-medium">Average Invoice Size</CardTitle>
            <div className="w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center text-slate-600">
              <DollarSign className="w-4 h-4" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(avgInvoiceAmount, displayCurrency)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Mean invoice amount</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Billing Timeline Area Chart */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-indigo-500" />
                6-Month Billing Timeline
              </CardTitle>
              <CardDescription>Compare total invoiced amount vs paid collections.</CardDescription>
            </div>
            <div className="flex items-center gap-4 text-xs font-medium">
              <div className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded bg-indigo-500 inline-block"></span>
                <span>Invoiced</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded bg-emerald-500 inline-block"></span>
                <span>Paid</span>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-2">
            <div className="relative">
              <svg viewBox="0 0 580 200" className="w-full overflow-visible">
                <defs>
                  <linearGradient id="gradInvoiced" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#6366f1" stopOpacity="0.2" />
                    <stop offset="100%" stopColor="#6366f1" stopOpacity="0" />
                  </linearGradient>
                  <linearGradient id="gradPaid" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity="0.2" />
                    <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
                  </linearGradient>
                </defs>

                {/* Y-axis gridlines */}
                {[0, 0.25, 0.5, 0.75, 1].map((pct, idx) => {
                  const y = paddingY + chartH * pct;
                  const val = maxVal * (1 - pct);
                  return (
                    <g key={idx}>
                      <line
                        x1={paddingX}
                        y1={y}
                        x2={paddingX + chartW}
                        y2={y}
                        stroke="#e2e8f0"
                        strokeDasharray="4 4"
                      />
                      <text x={paddingX - 10} y={y + 4} textAnchor="end" className="text-[10px] fill-muted-foreground font-sans">
                        {val >= 1000 ? `${(val / 1000).toFixed(0)}k` : val.toFixed(0)}
                      </text>
                    </g>
                  );
                })}

                {/* Invoiced Area & Line */}
                <path d={invoicedAreaPath} fill="url(#gradInvoiced)" />
                <path d={invoicedLinePath} fill="none" stroke="#6366f1" strokeWidth="2.5" strokeLinecap="round" />

                {/* Paid Area & Line */}
                <path d={paidAreaPath} fill="url(#gradPaid)" />
                <path d={paidLinePath} fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" />

                {/* Month Labels & Interaction Rectangles */}
                {points.map((p, idx) => (
                  <g key={idx}>
                    {/* Tick Mark on X-axis */}
                    <line x1={p.x} y1={paddingY + chartH} x2={p.x} y2={paddingY + chartH + 5} stroke="#cbd5e1" />
                    <text x={p.x} y={paddingY + chartH + 18} textAnchor="middle" className="text-[10px] fill-muted-foreground font-semibold">
                      {p.label}
                    </text>

                    {/* Data circle markers */}
                    <circle cx={p.x} cy={p.yInvoiced} r={hoveredPointIndex === idx ? 6 : 4} fill="#6366f1" stroke="#ffffff" strokeWidth="1.5" className="transition-all duration-100" />
                    <circle cx={p.x} cy={p.yPaid} r={hoveredPointIndex === idx ? 6 : 4} fill="#10b981" stroke="#ffffff" strokeWidth="1.5" className="transition-all duration-100" />

                    {/* Invisible hover regions */}
                    <rect
                      x={p.x - chartW / 10}
                      y={paddingY}
                      width={chartW / 5}
                      height={chartH}
                      fill="transparent"
                      className="cursor-pointer"
                      onMouseEnter={() => setHoveredPointIndex(idx)}
                      onMouseLeave={() => setHoveredPointIndex(null)}
                    />
                  </g>
                ))}
              </svg>

              {/* Hover Tooltip Card */}
              {hoveredPointIndex !== null && (
                <div
                  className="absolute bg-white/95 backdrop-blur-sm border shadow-lg rounded-lg p-3 text-xs space-y-1.5 z-20 transition-all pointer-events-none"
                  style={{
                    left: `${(points[hoveredPointIndex].x / 580) * 100}%`,
                    top: '10px',
                    transform: 'translateX(-50%)',
                  }}
                >
                  <p className="font-bold text-slate-800 border-b pb-1 mb-1">{points[hoveredPointIndex].label}</p>
                  <div className="flex justify-between gap-6">
                    <span className="text-muted-foreground">Invoiced:</span>
                    <span className="font-semibold text-indigo-600">
                      {formatCurrency(points[hoveredPointIndex].invoiced, displayCurrency)}
                    </span>
                  </div>
                  <div className="flex justify-between gap-6">
                    <span className="text-muted-foreground">Paid Collections:</span>
                    <span className="font-semibold text-emerald-600">
                      {formatCurrency(points[hoveredPointIndex].paid, displayCurrency)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Invoice Status Distribution Donut Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <PieChartIcon className="w-5 h-5 text-indigo-500" />
              Invoice Status
            </CardTitle>
            <CardDescription>Overall breakdown of statuses.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center justify-center pb-6">
            {totalInvoicesCount > 0 ? (
              <div className="flex flex-col sm:flex-row items-center gap-6 mt-2">
                {/* SVG Donut Graphic */}
                <div className="relative w-40 h-40">
                  <svg viewBox="0 0 160 160" className="w-full h-full transform -rotate-90">
                    <circle cx="80" cy="80" r="50" fill="transparent" stroke="#f1f5f9" strokeWidth="15" />
                    {donutSlices.map((slice, idx) => (
                      <circle
                        key={idx}
                        cx="80"
                        cy="80"
                        r="50"
                        fill="transparent"
                        stroke={slice.color}
                        strokeWidth="15"
                        strokeDasharray={slice.strokeDasharray}
                        strokeDashoffset={slice.strokeDashoffset}
                        transform={`rotate(${slice.rotation} 80 80)`}
                        className="transition-all duration-300"
                      />
                    ))}
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                    <span className="text-3xl font-extrabold text-slate-800">{totalInvoicesCount}</span>
                    <span className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider">Invoices</span>
                  </div>
                </div>

                {/* Legends */}
                <div className="space-y-2.5">
                  {donutSlices.map((cat, idx) => (
                    <div key={idx} className="flex items-center gap-2.5 text-sm">
                      <span className="w-3.5 h-3.5 rounded-full inline-block shrink-0" style={{ backgroundColor: cat.color }}></span>
                      <span className="font-medium text-slate-700">{cat.label}:</span>
                      <span className={`font-bold ${cat.textColor}`}>{cat.count}</span>
                      <span className="text-xs text-muted-foreground">({(cat.percent * 100).toFixed(0)}%)</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground text-sm">
                No billing activities for the selected currency.
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top Performers Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Top Customers by Invoiced Revenue */}
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-indigo-500" />
              Top Customers
            </CardTitle>
            <CardDescription>Clients ranked by overall billing revenue.</CardDescription>
          </CardHeader>
          <CardContent className="pt-2">
            {topCustomers.length === 0 ? (
              <p className="text-muted-foreground text-sm py-4 text-center">No customer data available.</p>
            ) : (
              <div className="space-y-5">
                {topCustomers.map((cust, idx) => {
                  const percentWidth = (cust.revenue / maxCustomerRevenue) * 100;
                  return (
                    <div key={idx} className="space-y-1.5">
                      <div className="flex justify-between items-center text-sm font-semibold text-slate-800">
                        <span className="truncate max-w-xs">{cust.name}</span>
                        <span>{formatCurrency(cust.revenue, displayCurrency)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-slate-100 h-2.5 rounded-full overflow-hidden">
                          <div
                            className="bg-indigo-500 h-full rounded-full transition-all duration-500"
                            style={{ width: `${percentWidth}%` }}
                          ></div>
                        </div>
                        <span className="text-xs text-muted-foreground font-medium w-8 text-right">
                          {cust.count} inv
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Billing Businesses */}
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Building2 className="w-5 h-5 text-indigo-500" />
              Top Billing Business Profiles
            </CardTitle>
            <CardDescription>Your billing business structures ranked by billing output.</CardDescription>
          </CardHeader>
          <CardContent className="pt-2">
            {topBusinesses.length === 0 ? (
              <p className="text-muted-foreground text-sm py-4 text-center">No business data available.</p>
            ) : (
              <div className="space-y-5">
                {topBusinesses.map((bus, idx) => {
                  const percentWidth = (bus.revenue / maxBusinessRevenue) * 100;
                  return (
                    <div key={idx} className="space-y-1.5">
                      <div className="flex justify-between items-center text-sm font-semibold text-slate-800">
                        <span className="truncate max-w-xs">{bus.name}</span>
                        <span>{formatCurrency(bus.revenue, displayCurrency)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-slate-100 h-2.5 rounded-full overflow-hidden">
                          <div
                            className="bg-emerald-500 h-full rounded-full transition-all duration-500"
                            style={{ width: `${percentWidth}%` }}
                          ></div>
                        </div>
                        <span className="text-xs text-muted-foreground font-medium w-8 text-right">
                          {bus.count} inv
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
